import { sortBy } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { ProgressCircle } from '@commons/ProgressCircle';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { COLUMN_TYPES } from '../../../../forecast/Forecast/SalesPlanning/datagrid/constants';
import { renderStandardHeader } from '../../../../forecast/Forecast/SalesPlanning/datagrid/customCellRenders';

import { TimeSlotHeader } from './shared/TimeSlotHeader/TimeSlotHeader';

import {
  InputCell,
  TimeSlotContainer,
  ToProduceCell,
  TotalContainer,
  TotalNumbersContainer,
} from './styledComponents';

import { ALLOWED_INPUT_KEYS } from '@commons/utils/numericInputKeys';

import { getSharedColumns, isFieldModified } from './shared/utils';
import { handleDOMFocusChangeOnEnterKeyPress } from './shared/focusHandlers';
import { NumberInputWithButtons } from './shared/NumberInputWithButtons/NumberInputWithButtons';

const DOM_FOCUS_NEXT_CELL_TYPE = {
  forecast: 'stock',
  stock: 'produced',
  produced: 'forecast',
};

const CELL_TYPES = {
  FORECAST: 'forecast',
  STOCK: 'stock',
  PRODUCED: 'produced',
};

const getNextCellType = (type, isForecastDisabled, isStockOrProducedDisabled) => {
  if (isStockOrProducedDisabled) {
    return CELL_TYPES.FORECAST;
  }

  if (isForecastDisabled) {
    return type === CELL_TYPES.PRODUCED ? CELL_TYPES.STOCK : CELL_TYPES.PRODUCED;
  }

  return DOM_FOCUS_NEXT_CELL_TYPE[type];
};

const handleDOMFocusChangeOnTabKeyPress = (
  rowId,
  timeSlotId,
  type,
  onFocusChange,
  timeSlotIndex,
  timeSlots,
  isForecastDisabled,
  isStockOrProducedDisabled,
) => {
  const nextTimeslotIndex = timeSlotIndex + 1;

  let timeSlotIdToUse = timeSlotId;

  /*
    When isStockOrProducedDisabled is true, only the forecast cell is selectable, requiring a timeslot change
    Otherwise we need to change timeslot only when type === 'produced'
  */
  const shouldChangeTimeslot = isStockOrProducedDisabled || type === CELL_TYPES.PRODUCED;

  if (shouldChangeTimeslot) {
    // If nextTimeslotIndex is greater than the length of the timeSlots array we want to focus on the first element of timeslot to have a loop
    if (nextTimeslotIndex >= timeSlots.length) {
      const nextTimeSlotId = timeSlots.length ? timeSlots[0].id : timeSlotId;
      timeSlotIdToUse = nextTimeSlotId;
      // If cell is produced (aka the last cell of the timeslot) we want to focus on the next timeslot
    } else {
      const nextTimeSlotId = timeSlots[nextTimeslotIndex].id;
      timeSlotIdToUse = nextTimeSlotId;
    }
  }

  const nextCellType = getNextCellType(type, isForecastDisabled, isStockOrProducedDisabled);

  const nextFocusId = `${rowId}-${timeSlotIdToUse}-${nextCellType}`;

  // Find and focus the next input element in the DOM
  const nextInput = document.querySelector(`input[data-focus-id="${nextFocusId}"]`);

  if (nextInput) {
    nextInput.select();
    nextInput.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    onFocusChange(nextFocusId);
  }
};

// Factory function to create keydown handler with proper closure
const createKeyDownHandler =
  ({
    rowId,
    timeSlotId,
    onFocusChange,
    timeSlotIndex,
    timeSlots,
    type,
    sortedVisibleRows,
    isForecastDisabled,
    isStockOrProducedDisabled,
  }) =>
  (e) => {
    // Prevent losing focus when using arrow keys
    e.stopPropagation();

    // Handle Enter and Escape keys
    if (e.key === 'Enter') {
      e.preventDefault();

      // Update the focus state
      handleDOMFocusChangeOnEnterKeyPress(
        rowId,
        timeSlotId,
        type,
        onFocusChange,
        sortedVisibleRows,
        'originalProductId',
      );

      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();

      // Update the focus state
      handleDOMFocusChangeOnTabKeyPress(
        rowId,
        timeSlotId,
        type,
        onFocusChange,
        timeSlotIndex,
        timeSlots,
        isForecastDisabled,
        isStockOrProducedDisabled,
      );

      return;
    }

    if (e.key === 'Escape') {
      // Cancel and revert to original value
      const originalValue = e.target.dataset.originalValue || '';
      e.target.value = originalValue;

      // Leave focus
      e.target.blur();
      onFocusChange(null);

      return;
    }

    // Only allow digits, backspace, delete, and navigation keys
    if (!ALLOWED_INPUT_KEYS.has(e.key)) {
      e.preventDefault();
    }
  };

// Component to display the 4 cells per timeslot
const TimeslotCell = ({
  row,
  timeSlotId,
  onStockChange,
  onProducedChange,
  onForecastChange,
  isForecastDisabled,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  originalData,
  timeSlotIndex,
  timeSlots,
  sortedVisibleRows,
}) => {
  const data = row.dataByTimeSlotIds?.[timeSlotId] || {
    forecast: null,
    inStock: null,
    produced: null,
  };

  const forecast = data.forecast;
  const inStock = data.inStock;
  const produced = data.produced;
  const toProduce = forecast - inStock;
  const isOperatorForecast = data.isOperatorForecast || false;

  // Check if each field has been modified
  const isForecastModified = isFieldModified(forecast, originalData, timeSlotId, 'forecast');
  const isStockModified = isFieldModified(inStock, originalData, timeSlotId, 'inStock');
  const isProducedModified = isFieldModified(produced, originalData, timeSlotId, 'produced');

  return (
    <TimeSlotContainer>
      {/* Forecast */}
      <InputCell
        cellType="forecast"
        hasBeenModified={isForecastModified}
        isDisabled={isForecastDisabled}
        isFocused={focusedInput === `${row.originalProductId}-${timeSlotId}-forecast`}
        isOperatorForecast={isOperatorForecast && !isForecastDisabled}
      >
        <NumberInputWithButtons
          createKeyDownHandler={createKeyDownHandler}
          isDisabled={isForecastDisabled}
          isFocused={focusedInput === `${row.originalProductId}-${timeSlotId}-forecast`}
          isForecastDisabled={isForecastDisabled}
          isStockOrProducedDisabled={isStockOrProducedDisabled}
          rowId={row.originalProductId}
          sortedVisibleRows={sortedVisibleRows}
          timeSlotId={timeSlotId}
          timeSlotIndex={timeSlotIndex}
          timeSlots={timeSlots}
          type="forecast"
          value={forecast}
          onFocusChange={onFocusChange}
          onValueChange={onForecastChange}
        />
      </InputCell>

      {/* Stock */}
      <InputCell
        cellType="stock"
        hasBeenModified={isStockModified}
        isDisabled={isStockOrProducedDisabled}
        isFocused={focusedInput === `${row.originalProductId}-${timeSlotId}-stock`}
      >
        <NumberInputWithButtons
          createKeyDownHandler={createKeyDownHandler}
          isDisabled={isStockOrProducedDisabled}
          isFocused={focusedInput === `${row.originalProductId}-${timeSlotId}-stock`}
          isForecastDisabled={isForecastDisabled}
          isStockOrProducedDisabled={isStockOrProducedDisabled}
          rowId={row.originalProductId}
          sortedVisibleRows={sortedVisibleRows}
          timeSlotId={timeSlotId}
          timeSlotIndex={timeSlotIndex}
          timeSlots={timeSlots}
          type="stock"
          value={inStock}
          onFocusChange={onFocusChange}
          onValueChange={onStockChange}
        />
      </InputCell>

      {/* To produce */}
      <ToProduceCell cellType="toProduce" isDisabled={true}>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}>
          {toProduce >= 0 ? toProduce : 0}
        </Text>
      </ToProduceCell>

      {/* Produced */}
      <InputCell
        cellType="produced"
        hasBeenModified={isProducedModified}
        isDisabled={isStockOrProducedDisabled}
        isFocused={focusedInput === `${row.originalProductId}-${timeSlotId}-produced`}
      >
        <NumberInputWithButtons
          createKeyDownHandler={createKeyDownHandler}
          isDisabled={isStockOrProducedDisabled}
          isFocused={focusedInput === `${row.originalProductId}-${timeSlotId}-produced`}
          isForecastDisabled={isForecastDisabled}
          isStockOrProducedDisabled={isStockOrProducedDisabled}
          rowId={row.originalProductId}
          sortedVisibleRows={sortedVisibleRows}
          timeSlotId={timeSlotId}
          timeSlotIndex={timeSlotIndex}
          timeSlots={timeSlots}
          type="produced"
          value={produced}
          onFocusChange={onFocusChange}
          onValueChange={onProducedChange}
        />
      </InputCell>
    </TimeSlotContainer>
  );
};

// Generate columns for timeSlots
const generateProductTimeSlotColumns = (
  timeSlots,
  onStockChange,
  onProducedChange,
  onForecastChange,
  isForecastDisabled,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  originalProductsData,
  sortedVisibleRows,
  onFillTimeSlotProduction,
  onClearTimeSlotQuantities,
) => {
  // Sort timeSlots by startTime
  const sortedTimeSlots = sortBy(timeSlots, 'startTime');

  // Only show menu if there are multiple time slots
  const showMenu = sortedTimeSlots.length > 1;

  return sortedTimeSlots.map((timeSlot, timeSlotIndex) => ({
    field: `timeSlot_${timeSlot.id}`,
    headerName: '', // No headerName because we use renderHeader
    minWidth: 300,
    flex: 1,
    disableColumnMenu: true,
    sortable: false,
    resizable: false,
    filterable: false,
    disableReorder: true,
    renderHeader: () => (
      <TimeSlotHeader
        showMenu={showMenu}
        timeSlot={timeSlot}
        onClearTimeSlotQuantities={onClearTimeSlotQuantities}
        onFillTimeSlotProduction={onFillTimeSlotProduction}
      />
    ),
    renderCell: ({ row }) => {
      const originalData = originalProductsData?.[row.originalProductId];

      return (
        <TimeslotCell
          focusedInput={focusedInput}
          isForecastDisabled={isForecastDisabled}
          isStockOrProducedDisabled={isStockOrProducedDisabled}
          originalData={originalData}
          row={row}
          sortedVisibleRows={sortedVisibleRows}
          timeSlotId={timeSlot.id}
          timeSlotIndex={timeSlotIndex}
          timeSlots={sortedTimeSlots}
          onFocusChange={onFocusChange}
          onForecastChange={onForecastChange}
          onProducedChange={onProducedChange}
          onStockChange={onStockChange}
        />
      );
    },
  }));
};

const getProductSimpleViewColumns = (
  singleTimeslotId,
  onStockChange,
  onProducedChange,
  onForecastChange,
  isForecastDisabled,
  isStockOrProducedDisabled,
  focusedInput,
  onFocusChange,
  commonColumnsProps,
  originalProductsData,
  sortedVisibleRows,
) => {
  const forecastColumn = {
    ...commonColumnsProps,
    field: 'forecast',
    headerName: i18next.t('PRODUCTION.PLANNING.FORECAST', { count: 1 }),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => params.row.dataByTimeSlotIds?.[singleTimeslotId]?.forecast,
    renderCell: ({ row }) => {
      const data = (singleTimeslotId && row.dataByTimeSlotIds?.[singleTimeslotId]) || {
        forecast: null,
      };

      const isOperatorForecast = data.isOperatorForecast || false;
      const originalData = originalProductsData?.[row.originalProductId];
      const isForecastModified = isFieldModified(
        data.forecast,
        originalData,
        singleTimeslotId,
        'forecast',
      );

      return (
        <InputCell
          cellType="forecast"
          hasBeenModified={isForecastModified}
          isDisabled={isForecastDisabled}
          isFocused={focusedInput === `${row.originalProductId}-${singleTimeslotId}-forecast`}
          isOperatorForecast={isOperatorForecast && !isForecastDisabled}
        >
          <NumberInputWithButtons
            createKeyDownHandler={createKeyDownHandler}
            isDisabled={isForecastDisabled}
            isFocused={focusedInput === `${row.originalProductId}-${singleTimeslotId}-forecast`}
            isForecastDisabled={isForecastDisabled}
            isStockOrProducedDisabled={isStockOrProducedDisabled}
            rowId={row.originalProductId}
            sortedVisibleRows={sortedVisibleRows}
            timeSlotId={singleTimeslotId}
            timeSlotIndex={0}
            timeSlots={[]}
            type="forecast"
            value={data.forecast}
            onFocusChange={onFocusChange}
            onValueChange={onForecastChange}
          />
        </InputCell>
      );
    },
  };

  const stockColumn = {
    ...commonColumnsProps,
    field: 'stock',
    headerName: i18next.t('PRODUCTION.PLANNING.STOCK'),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => params.row.dataByTimeSlotIds?.[singleTimeslotId]?.inStock,
    renderCell: ({ row }) => {
      let inStock = null;

      if (singleTimeslotId && row.dataByTimeSlotIds?.[singleTimeslotId]?.inStock >= 0) {
        inStock = row.dataByTimeSlotIds[singleTimeslotId].inStock;
      }

      return (
        <InputCell
          cellType="stock"
          hasBeenModified={!!inStock}
          isDisabled={isStockOrProducedDisabled}
          isFocused={focusedInput === `${row.originalProductId}-${singleTimeslotId}-stock`}
        >
          <NumberInputWithButtons
            createKeyDownHandler={createKeyDownHandler}
            isDisabled={isStockOrProducedDisabled}
            isFocused={focusedInput === `${row.originalProductId}-${singleTimeslotId}-stock`}
            isForecastDisabled={isForecastDisabled}
            isStockOrProducedDisabled={isStockOrProducedDisabled}
            rowId={row.originalProductId}
            sortedVisibleRows={sortedVisibleRows}
            timeSlotId={singleTimeslotId}
            timeSlotIndex={0}
            timeSlots={[]}
            type="stock"
            value={inStock}
            onFocusChange={onFocusChange}
            onValueChange={onStockChange}
          />
        </InputCell>
      );
    },
  };

  const toProduceColumn = {
    ...commonColumnsProps,
    field: 'toProduce',
    headerName: i18next.t('PRODUCTION.PLANNING.TO_PRODUCE'),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => {
      const { toProduce } = getProducedAndToProduce(
        params.row.dataByTimeSlotIds?.[singleTimeslotId],
      );

      return toProduce;
    },
    renderCell: ({ row }) => {
      const { toProduce } = getProducedAndToProduce(row.dataByTimeSlotIds?.[singleTimeslotId]);

      return (
        <ToProduceCell cellType="toProduce" isDisabled={true}>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_12}>
            {toProduce}
          </Text>
        </ToProduceCell>
      );
    },
  };

  const producedColumn = {
    ...commonColumnsProps,
    field: 'produced',
    headerName: i18next.t('PRODUCTION.PLANNING.PRODUCED'),
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    resizable: false,
    type: 'number',
    valueGetter: (params) => params.row.dataByTimeSlotIds?.[singleTimeslotId]?.produced,
    renderCell: ({ row }) => {
      let produced = null;

      if (singleTimeslotId && row.dataByTimeSlotIds?.[singleTimeslotId]?.produced >= 0) {
        produced = row.dataByTimeSlotIds[singleTimeslotId].produced;
      }

      return (
        <InputCell
          cellType="produced"
          hasBeenModified={!!produced}
          isDisabled={isStockOrProducedDisabled}
          isFocused={focusedInput === `${row.originalProductId}-${singleTimeslotId}-produced`}
        >
          <NumberInputWithButtons
            createKeyDownHandler={createKeyDownHandler}
            isDisabled={isStockOrProducedDisabled}
            isFocused={focusedInput === `${row.originalProductId}-${singleTimeslotId}-produced`}
            isForecastDisabled={isForecastDisabled}
            isStockOrProducedDisabled={isStockOrProducedDisabled}
            rowId={row.originalProductId}
            sortedVisibleRows={sortedVisibleRows}
            timeSlotId={singleTimeslotId}
            timeSlotIndex={0}
            timeSlots={[]}
            type="produced"
            value={produced}
            onFocusChange={onFocusChange}
            onValueChange={onProducedChange}
          />
        </InputCell>
      );
    },
  };

  return [forecastColumn, stockColumn, toProduceColumn, producedColumn];
};

const getProducedAndToProduce = (timeSlotData) => {
  const forecast = timeSlotData?.forecast || 0;
  const inStock = timeSlotData?.inStock || 0;
  const produced = timeSlotData?.produced !== undefined ? timeSlotData.produced : null;

  // toProduce = forecast - inStock (but minimum 0)
  const toProduce = Math.max(0, forecast - inStock);

  return {
    produced,
    toProduce,
  };
};

// Component for Total cell
const TotalCell = ({ totalToProduce, totalProduced, completionPercentage, colDef }) => {
  const showText = colDef?.computedWidth >= 120;

  return (
    <TotalContainer style={{ justifyContent: showText ? 'space-between' : 'center' }}>
      {showText && (
        <TotalNumbersContainer>
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
            {totalToProduce === null ? '-' : totalToProduce} {i18next.t('GENERAL.MINIFIED_UNIT')} |{' '}
            {totalProduced === null ? '-' : totalProduced} {i18next.t('GENERAL.MINIFIED_UNIT')}
          </Text>
        </TotalNumbersContainer>
      )}
      <ProgressCircle
        customSize={48}
        font={ENUM_FONTS.TEXT_SMALL_BOLD}
        percentage={completionPercentage}
      />
    </TotalContainer>
  );
};

const calculateTotals = (row, singleTimeslotId, isSimpleView) => {
  let totalToProduce = 0;
  let totalProduced = null;

  if (isSimpleView && singleTimeslotId) {
    // Simple view: use single timeslot data
    const { toProduce, produced } = getProducedAndToProduce(
      row.dataByTimeSlotIds?.[singleTimeslotId],
    );

    totalToProduce = toProduce;
    totalProduced = produced;
  } else {
    // Multi-timeslot view: sum all timeSlots
    if (row.dataByTimeSlotIds) {
      Object.values(row.dataByTimeSlotIds).forEach((timeSlotData) => {
        const { toProduce, produced } = getProducedAndToProduce(timeSlotData);

        totalToProduce += toProduce;

        if (produced !== null) {
          totalProduced = (totalProduced || 0) + produced;
        }
      });
    }
  }

  // Calculate completion percentage
  const completionPercentage = totalToProduce > 0 ? (totalProduced / totalToProduce) * 100 : 0;

  return { totalToProduce, totalProduced, completionPercentage };
};

const getTotalColumn = (commonColumnsProps, singleTimeslotId, isSimpleView) => ({
  ...commonColumnsProps,
  field: 'total',
  type: 'number',
  headerName: i18next.t('GENERAL.TOTAL'),
  minWidth: 100,
  maxWidth: 200,
  headerAlign: 'left',
  sortable: true,
  resizable: true,
  valueGetter: (params) => {
    const { completionPercentage } = calculateTotals(params.row, singleTimeslotId, isSimpleView);

    return completionPercentage;
  },
  renderCell: (params) => {
    const { colDef } = params;
    const { totalToProduce, totalProduced, completionPercentage } = calculateTotals(
      params.row,
      singleTimeslotId,
      isSimpleView,
    );

    return (
      <TotalCell
        colDef={colDef}
        completionPercentage={completionPercentage}
        totalProduced={totalProduced}
        totalToProduce={totalToProduce}
      />
    );
  },
});

export const getProductsColumns = ({
  timeSlots = [],
  onStockChange,
  onProducedChange,
  onForecastChange,
  // Todo INP-1649 - update these values depending on user rights
  isForecastDisabled = false,
  isStockOrProducedDisabled = false,
  focusedInput,
  onFocusChange,
  originalProductsData = {},
  formattedProductsData = [],
  sortedVisibleRows = [],
  onFillTimeSlotProduction,
  onClearTimeSlotQuantities,
} = {}) => {
  // Determine if we should use no timeslot view (single timeslot without name, i.e no active timeslot)
  const useSimpleView = timeSlots.length === 1 && !timeSlots[0].name;

  // Get the single timeslot ID for simple view
  const singleTimeslotId = timeSlots.length === 1 ? timeSlots[0].id : null;

  const commonColumnsProps = {
    disableReorder: true,
    disableColumnMenu: true,
    renderHeader: renderStandardHeader,
  };

  const { nameColumn, categoryColumn } = getSharedColumns(
    commonColumnsProps,
    formattedProductsData,
    COLUMN_TYPES.PRODUCTS,
  );

  if (useSimpleView) {
    const simpleViewColumns = getProductSimpleViewColumns(
      singleTimeslotId,
      onStockChange,
      onProducedChange,
      onForecastChange,
      isForecastDisabled,
      isStockOrProducedDisabled,
      focusedInput,
      onFocusChange,
      commonColumnsProps,
      originalProductsData,
      sortedVisibleRows,
    );

    // Simple view: return columns for single/unnamed timeslot
    return [
      nameColumn,
      categoryColumn,
      ...simpleViewColumns,
      getTotalColumn(commonColumnsProps, singleTimeslotId, true),
    ];
  } else {
    // Multi-timeslot view: generate timeslot columns
    const timeSlotColumns = generateProductTimeSlotColumns(
      timeSlots,
      onStockChange,
      onProducedChange,
      onForecastChange,
      isForecastDisabled,
      isStockOrProducedDisabled,
      focusedInput,
      onFocusChange,
      originalProductsData,
      sortedVisibleRows,
      onFillTimeSlotProduction,
      onClearTimeSlotQuantities,
    );

    return [
      nameColumn,
      categoryColumn,
      ...timeSlotColumns,
      getTotalColumn(commonColumnsProps, null, false),
    ];
  }
};
